import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from './cn'
import { TONES, type Tone } from './tone'

const LAYOUTS = {
  /** Inside a pane or a card body. */
  block: 'rounded-lg p-3 gap-1',
  /** Spanning a pane, under its header — carries a rule instead of a radius. */
  bar: 'border-b border-current/15 px-5 py-2.5 gap-3',
  /**
   * Spanning the viewport above a page: the page gutter rather than the pane
   * one, and the content held to the page's own measure so it lines up with
   * what is under it.
   */
  banner: 'border-b border-current/15 px-4 py-2.5 gap-3 lg:px-8',
  /**
   * A result standing on its own: the tone in the border and the heading, the
   * body on the card ground so long text stays readable on it.
   */
  card: 'rounded-lg border border-current/25 overflow-hidden',
} as const

/**
 * A tinted block that states a condition the surrounding screen is in: a
 * resolution warning, a draft, a formula that will not evaluate.
 *
 * One component rather than a tinted `div` per screen, so every warning in
 * Internal has the same ground, the same ink and the same spacing — and a
 * restyle is a diff here.
 */
export function Callout({
  tone = 'warning',
  layout = 'block',
  icon: Icon,
  title,
  /** Sits at the trailing edge — the action that resolves the condition. */
  action,
  className,
  children,
}: {
  tone?: Tone
  layout?: keyof typeof LAYOUTS
  icon?: LucideIcon
  title?: string
  action?: ReactNode
  className?: string
  children?: ReactNode
}) {
  const heading = title ? (
    <span className="flex flex-row items-center gap-1.5 text-xs font-medium">
      {Icon ? <Icon size={13} className="shrink-0" aria-hidden /> : null}
      {title}
    </span>
  ) : null

  if (layout === 'card') {
    return (
      <div className={cn(LAYOUTS.card, TONES[tone], className)}>
        {heading ? (
          <div className="flex flex-row items-center gap-2 border-b border-current/20 px-4 py-2.5 text-sm font-semibold">
            {Icon ? <Icon size={15} aria-hidden /> : null}
            {title}
            <span className="flex-1" />
            {action}
          </div>
        ) : null}
        <div className="bg-card px-4 py-3 text-xs text-foreground-2">
          {children}
        </div>
      </div>
    )
  }

  if (layout === 'bar' || layout === 'banner') {
    const row = (
      <>
        <span className="flex flex-1 flex-col gap-1 text-xs">
          {heading}
          {children}
        </span>
        {action}
      </>
    )

    return (
      <div
        className={cn(
          'flex flex-row items-center',
          LAYOUTS[layout],
          TONES[tone],
          className,
        )}
      >
        {layout === 'banner' ? (
          <span className="mx-auto flex w-full max-w-screen-xl flex-row items-center gap-3">
            {row}
          </span>
        ) : (
          row
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex flex-col text-xs',
        LAYOUTS.block,
        TONES[tone],
        className,
      )}
    >
      {heading}
      {children}
      {action}
    </div>
  )
}
